import { useId } from 'react'
import type { MascotAppearance } from './mascot-appearance'
import styles from './DonorMascot.module.css'

/** Large green eyes, pink round glasses and a tiny split smile from the reference. */
export function SheepFace({
  appearance: a,
  eyeColor,
}: {
  appearance: MascotAppearance
  eyeColor: string
}) {
  const id = `sheep-eyes-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`
  const droop = Math.min(0.7, a.fatigue * 0.55 + a.sickness * 0.25)
  const closed = a.sleeping || a.delighted || a.yawning
  return (
    <g className={styles.face} data-sheep-face>
      <defs>
        <clipPath id={id}>
          <path
            d={`M-20 ${-19 + droop * 28}Q0 ${-28 + droop * 30} 20 ${-19 + droop * 28}V28H-20Z`}
          />
        </clipPath>
      </defs>
      <g fill="#e69a9f" opacity={0.3 + a.sickness * 0.25}>
        <ellipse cx="73" cy="137" rx="13" ry="6" />
        <ellipse cx="169" cy="137" rx="13" ry="6" />
      </g>
      {[87, 153].map((x, i) => (
        <g key={x} transform={`translate(${x} 113)`}>
          <path
            d={`M-10 ${-28 + a.concern * (i ? -3 : 3)}q10 ${-5 - a.concern * 5} 17 1`}
            fill="none"
            stroke="#92594f"
            strokeWidth="5"
            strokeLinecap="round"
          />
          {closed ? (
            <g fill="none" stroke="#7a4946" strokeWidth="2.8" strokeLinecap="round">
              <path d={a.delighted ? 'M-13 2q13-18 26 0' : 'M-13 0q13 14 26 0'} />
              <path d="m-13 1-4-3m30 3 4-3" strokeWidth="1.8" />
            </g>
          ) : (
            <g className={styles.blink}>
              <g clipPath={`url(#${id})`}>
                <ellipse rx="19" ry="24" fill="#fffdf6" />
                <g className={styles.gaze}>
                  <ellipse cx={i ? -1 : 1} cy="1" rx="14" ry="21" fill={eyeColor} />
                  <path
                    d="M-10 12Q0 25 11 11"
                    fill="none"
                    stroke="#bde383"
                    strokeWidth="4"
                  />
                  <ellipse cx={i ? -1 : 1} cy="-1" rx="6.8" ry="12" fill="#694537" />
                  <ellipse cx="-5" cy="-11" rx="5" ry="7" fill="#fffef8" />
                  <circle cx="7" cy="11" r="3.5" fill="#fffef8" />
                </g>
              </g>
              <path
                d={`M-18 ${-13 + droop * 19}Q0 ${-31 + droop * 29} 18 ${-13 + droop * 19}`}
                fill="none"
                stroke="#7a4946"
                strokeWidth="2.8"
                strokeLinecap="round"
              />
              <path
                d={i ? 'M15-15q6 1 8-6m-4 12q6 0 7-5' : 'M-15-15q-6 1-8-6m4 12q-6 0-7-5'}
                fill="none"
                stroke="#7a4946"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </g>
          )}
        </g>
      ))}
      <g data-sheep-glasses fill="none" stroke="#d88f85" strokeWidth="1.9">
        <circle cx="87" cy="113" r="29" />
        <circle cx="153" cy="113" r="29" />
        <path d="M116 108q4-5 8 0m-66-1-6-5m130 5 5-4" />
        <path
          d="M67 126q5 8 14 9m52-10q4 9 14 11"
          stroke="#fff9ee"
          strokeWidth="2.6"
          opacity=".7"
        />
      </g>
      <path d="M114 139q6-4 12 0l-6 6Z" fill="#d98c9c" />
      {a.yawning ? (
        <g className={styles.yawnMouth}>
          <ellipse cx="120" cy="151" rx="8" ry="10" fill="#754740" />
          <ellipse cx="120" cy="156" rx="5" ry="3" fill="#e89fa2" />
        </g>
      ) : a.delighted ? (
        <g>
          <path d="M109 148q11 5 22 0-2 13-11 13t-11-13" fill="#754740" />
          <path d="M113 156q7-6 14 0-6 7-14 0" fill="#e89fa2" />
        </g>
      ) : (
        <g stroke="#754740" strokeWidth="2.2" strokeLinecap="round" fill="none">
          <path d="M120 145v5" />
          <path
            d={`M109 148q5 ${8 - a.concern * 12} 11 2 6 ${6 - a.concern * 12} 11-2`}
          />
        </g>
      )}
      {a.sickness > 0 && (
        <path
          d="M179 91q-7 11-1 13 7 0 1-13"
          fill="#b5dce0"
          stroke="#8bb6bd"
          opacity={a.sickness}
        />
      )}
    </g>
  )
}
